import { Action, createPropertySelectors, PropertySelectors, Selector, State, StateContext, Store } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { ClassTreeColumn } from '../../models/customizable-class-tree/class-tree-column.model';
import { ResetStateDefaults } from '../base-action-class.model';
import { CustomizableClassTreeAction } from './customizable-class-tree.action';
import { finalize, forkJoin, Observable, of, throwError } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { patch } from '@ngxs/store/operators';
import { ClassTreeNodeMap } from '../../models/customizable-class-tree/class-tree-node-map.model';
import { calcDefaultTimeoutOf } from '../../services/client/request-client.service';
import {
  ClassTreeCommand,
  ClassTreeDeleteCommand,
  ClassTreeMoveCommand,
  ClassTreeRenameCommand
} from '../../models/customizable-class-tree/class-tree-operation.model';
import { ClassTreeParentChildrenMap } from '../../models/customizable-class-tree/class-tree-parent-children-map.model';
import { ClassTreeCommandType } from '../../enums/custom-class-tree/class-tree-operation.enum';
import { CustomizableClassTreeRequestService } from '../../services/request/customizable-class-tree-request.service';
import { ClassTreeNodeEntry } from '../../models/customizable-class-tree/class-tree-node-entry.model';
import { TranslationCodeView } from '../../models/translation/TranslationCodeView';
import { CompoApp } from '../../services/CompoApp';
import {
  CustomizableClassTreeInternalCodeEnum
} from '../../models/customizable-class-tree/customizable-class-tree-internal-code.enum';
import { ClassTreeNodeEntryInfo } from '../../models/customizable-class-tree/class-tree-node-entry-info-model';
import Languages from '../../../assets/i18n/Languages';

/**
 * @commands - the tree data are not manipulated directly, but the changes are stored in the commands array
 * via which the data is manipulated in getter methods.
 */
interface CustomizableClassTreeStateModel {
  orgId: string;
  treeId: string;
  columnParentIds: string[];
  arbitraryParentChildrenMap: ClassTreeParentChildrenMap;
  arbitraryNodes: ClassTreeNodeMap;
  allClassNodeInfos: ClassTreeNodeEntryInfo[];
  requiredClassIds: string[];
  dragInProgress: boolean;
  draggedClassIds: string[];
  commands: ClassTreeCommand[];
  undo: number;
  saveInProgress: boolean;
  treeInternalCode: CustomizableClassTreeInternalCodeEnum | null;
  initialized: boolean;
}

const getDefaults = (): CustomizableClassTreeStateModel => ({
  orgId: null,
  treeId: null,
  columnParentIds: [],
  arbitraryNodes: {},
  arbitraryParentChildrenMap: {},
  allClassNodeInfos: [],
  requiredClassIds: [],
  dragInProgress: false,
  draggedClassIds: [],
  commands: [],
  undo: 0,
  saveInProgress: false,
  treeInternalCode: null,
  initialized: false,
});

@State<CustomizableClassTreeStateModel>({
  name: 'customizableClassTree',
  defaults: getDefaults()
})
@Injectable()
export class CustomizableClassTreeXsState {

  //region CONSTANTS
  private static readonly ROOT_KEY: string = 'ROOT';
  private static readonly NEW_CLASS_ID_PREFIX: string = 'NEW_';
  private static readonly SKELETON_COUNT: number = 10;
  //endregion

  //region INJECTIONS
  private readonly app: CompoApp = inject(CompoApp);
  private readonly store: Store = inject(Store);
  private readonly requestService: CustomizableClassTreeRequestService = inject(CustomizableClassTreeRequestService);
  //endregion

  //region PRIVATE SELECTORS
  private static readonly propertySelectors: PropertySelectors<CustomizableClassTreeStateModel>
    = createPropertySelectors<CustomizableClassTreeStateModel>(CustomizableClassTreeXsState);
  //endregion

  //region SELECTORS
  static readonly orgId = CustomizableClassTreeXsState.propertySelectors.orgId;
  static readonly treeId = CustomizableClassTreeXsState.propertySelectors.treeId;
  static readonly columnParentIds = CustomizableClassTreeXsState.propertySelectors.columnParentIds;
  static readonly allClassNodeInfos = CustomizableClassTreeXsState.propertySelectors.allClassNodeInfos;
  static readonly dragInProgress = CustomizableClassTreeXsState.propertySelectors.dragInProgress;
  static readonly draggedClassIds = CustomizableClassTreeXsState.propertySelectors.draggedClassIds;
  static readonly saveInProgress = CustomizableClassTreeXsState.propertySelectors.saveInProgress;
  static readonly treeInternalCode = CustomizableClassTreeXsState.propertySelectors.treeInternalCode;
  static readonly initialized = CustomizableClassTreeXsState.propertySelectors.initialized;

  @Selector([CustomizableClassTreeXsState.propertySelectors.columnParentIds])
  static columnIndices(columnParentIds: string[]): number[] {
    // Add +1 to the column count because the root key is not included in the colum list
    return Array.from({ length: (columnParentIds?.length ?? 0) + 1 }, (_, i) => i);
  }

  @Selector([
    CustomizableClassTreeXsState.propertySelectors.arbitraryNodes,
    CustomizableClassTreeXsState.propertySelectors.commands,
    CustomizableClassTreeXsState.propertySelectors.undo
  ])
  static nodes(
    arbitraryNodes: ClassTreeNodeMap,
    commands: ClassTreeCommand[],
    undo: number
  ): Map<string, ClassTreeNodeEntry> {
    return CustomizableClassTreeXsState.getNodesWithCommands(
      arbitraryNodes,
      commands,
      undo
    );
  }

  @Selector([
    CustomizableClassTreeXsState.propertySelectors.arbitraryNodes
  ])
  static allNodes(
    arbitraryNodes: ClassTreeNodeMap
  ): ClassTreeNodeEntry[] {
    return Object.values(arbitraryNodes);
  }

  @Selector([
    CustomizableClassTreeXsState.propertySelectors.arbitraryParentChildrenMap,
    CustomizableClassTreeXsState.propertySelectors.commands,
    CustomizableClassTreeXsState.propertySelectors.undo
  ])
  static parentChildrenMap(
    arbitraryParentChildrenMap: ClassTreeParentChildrenMap,
    commands: ClassTreeCommand[],
    undo: number
  ): Map<string, (string | null)[]> {
    return CustomizableClassTreeXsState.getParentChildrenMapWithCommands(
      arbitraryParentChildrenMap,
      commands,
      undo
    );
  }

  @Selector([CustomizableClassTreeXsState.parentChildrenMap])
  static childParentMap(parentChildrenMap: Map<string, (string | null)[]>): Map<string, string> {
    return CustomizableClassTreeXsState.getChildParentMap(parentChildrenMap);
  }

  @Selector([
    CustomizableClassTreeXsState.propertySelectors.arbitraryNodes,
    CustomizableClassTreeXsState.parentChildrenMap
  ])
  static allClassIdsInTree(
    nodes: ClassTreeNodeMap,
    parentChildrenMap: Map<string, (string | null)[]>
  ): Set<string> {
    return CustomizableClassTreeXsState.getAllClassIdsInTree(nodes, parentChildrenMap);
  }

  @Selector([CustomizableClassTreeXsState.propertySelectors.requiredClassIds])
  static requiredClassIds(
    requiredClassIds: string[]
  ): Set<string> {
    return new Set(requiredClassIds);
  }

  @Selector([
    CustomizableClassTreeXsState.propertySelectors.requiredClassIds,
    CustomizableClassTreeXsState.allClassIdsInTree
  ])
  static isRequiredClassNotInTree(
    requiredClassIds: string[],
    allClassIdsInTree: Set<string>
  ): boolean {
    for (const requiredClassId of requiredClassIds) {
      if (!allClassIdsInTree.has(requiredClassId)) {
        return true;
      }
    }
    return false;
  }

  @Selector([
    CustomizableClassTreeXsState.propertySelectors.arbitraryNodes,
    CustomizableClassTreeXsState.propertySelectors.allClassNodeInfos
  ])
  static allProductClassNamesUppercase(
    arbitraryNodes: ClassTreeNodeMap,
    allClassNodeInfos: ClassTreeNodeEntryInfo[]
  ): Map<string, Set<string>> {
    const nameMap: Map<string, Set<string>> = new Map();
    for (const language of Languages.availableLanguages) {
      nameMap.set(language, new Set());
    }
    for (const arbitraryNode of Object.values(arbitraryNodes)) {
      if (arbitraryNode.isProductClass) {
        for (const language of Languages.availableLanguages) {
          nameMap.get(language).add(arbitraryNode.nameTranslation?.translations[language]?.toUpperCase());
        }
      }
    }
    for (const classNodeInfo of allClassNodeInfos) {
      if (classNodeInfo.isProductClass) {
        for (const language of Languages.availableLanguages) {
          nameMap.get(language).add(classNodeInfo.nameTranslation?.translations[language]?.toUpperCase());
        }
      }
    }
    return nameMap;
  }

  @Selector([
    CustomizableClassTreeXsState.propertySelectors.columnParentIds,
    CustomizableClassTreeXsState.parentChildrenMap
  ])
  static columns(
    columnParentIds: string[],
    parentChildrenMap: Map<string, (string | null)[]>
  ): ClassTreeColumn[] {
    const columns: ClassTreeColumn[] = [];
    columns.push({
      parentId: null,
      classIds: parentChildrenMap?.get(CustomizableClassTreeXsState.ROOT_KEY) ?? []
    });
    for (const columnParentId of columnParentIds ?? []) {
      columns.push({
        parentId: columnParentId,
        classIds: parentChildrenMap?.get(columnParentId) ?? []
      });
    }
    return columns;
  }

  @Selector([
    CustomizableClassTreeXsState.propertySelectors.commands,
    CustomizableClassTreeXsState.propertySelectors.undo,
    CustomizableClassTreeXsState.propertySelectors.saveInProgress,
  ])
  static undoEnabled(
    commands: ClassTreeCommand[],
    undo: number,
    saveInProgress: boolean
  ): boolean {
    if (saveInProgress) {
      return false;
    }

    return commands?.length > undo;
  }

  @Selector([
    CustomizableClassTreeXsState.propertySelectors.undo,
    CustomizableClassTreeXsState.propertySelectors.saveInProgress,
  ])
  static redoEnabled(
    undo: number,
    saveInProgress: boolean
  ): boolean {
    if (saveInProgress) {
      return false;
    }

    return undo > 0;
  }

  @Selector([
    CustomizableClassTreeXsState.propertySelectors.commands,
    CustomizableClassTreeXsState.propertySelectors.undo
  ])
  static hasChanges(
    commands: ClassTreeCommand[],
    undo: number
  ): boolean {
    return commands?.length > undo;
  }
  //endregion

  //region ACTIONS
  @Action(CustomizableClassTreeAction.LoadClassTree)
  public loadTree(
    ctx: StateContext<CustomizableClassTreeStateModel>,
    action: CustomizableClassTreeAction.LoadClassTree
  ): Observable<void> {
    if (!action.orgId) {
      return throwError(() => new Error('Org id cannot be null!'));
    }

    if (ctx.getState().saveInProgress) {
      return throwError(() => new Error('Save in progress!'));
    }

    ctx.setState({
      ...getDefaults(),
      orgId: action.orgId,
      treeId: action.treeId,
      treeInternalCode: action.treeInternalCode,
      arbitraryParentChildrenMap: {
        [CustomizableClassTreeXsState.ROOT_KEY]: new Array(CustomizableClassTreeXsState.SKELETON_COUNT).fill(null)
      },
    });

    return this.fetchTree(ctx);
  }

  @Action(CustomizableClassTreeAction.ToggleColumn)
  public toggleColumns(
    ctx: StateContext<CustomizableClassTreeStateModel>,
    action: CustomizableClassTreeAction.ToggleColumn
  ): Observable<any> {
    if (!action.classId) {
      return throwError(() => new Error('Class id cannot be null!'));
    }

    if (ctx.getState().saveInProgress) {
      return throwError(() => new Error('Save in progress!'));
    }

    if (!ctx.getState().orgId) {
      return throwError(() => new Error('Org id cannot be null!'));
    }

    // Close node
    const columnParentIds: string[] = ctx.getState().columnParentIds ?? [];
    if (columnParentIds.includes(action.classId)) {
      ctx.patchState({
        columnParentIds: columnParentIds.slice(0, columnParentIds.indexOf(action.classId))
      });
      return of(null);
    }

    // Open node
    const parentChildrenMap: Map<string, (string | null)[]>
      = CustomizableClassTreeXsState.getParentChildrenMapWithCommands(
        ctx.getState().arbitraryParentChildrenMap,
        ctx.getState().commands,
        ctx.getState().undo
      );

    const parentIndex: number = columnParentIds.findIndex((id: string) =>
      parentChildrenMap?.get(id)?.includes(action.classId)
    );

    const newColumnParentIds: string[] = [
      ...columnParentIds.slice(0, parentIndex + 1),
      action.classId
    ];

    if (parentChildrenMap.has(action.classId)) {
      ctx.patchState({columnParentIds: newColumnParentIds});
      return of(null);
    } else {
      ctx.setState(patch<CustomizableClassTreeStateModel>({
        columnParentIds: newColumnParentIds,
        arbitraryParentChildrenMap: patch<CustomizableClassTreeStateModel['arbitraryParentChildrenMap']>({
          [action.classId]: new Array(CustomizableClassTreeXsState.SKELETON_COUNT).fill(null)
        }),
      }));

      const childrenIds = parentChildrenMap.get(action.classId);


      return of(childrenIds.map(id => ctx.getState().arbitraryNodes[id]));
    }
  }

  @Action(CustomizableClassTreeAction.ToggleDragSelection)
  public toggleDragSelection(
    ctx: StateContext<CustomizableClassTreeStateModel>,
    action: CustomizableClassTreeAction.ToggleDragSelection
  ): void {
    if (!action.classId) {
      throw new Error('Class id cannot be null!');
    }

    if (ctx.getState().saveInProgress) {
      throw new Error('Save in progress!');
    }

    if (!ctx.getState().arbitraryNodes.hasOwnProperty(action.classId)) {
      if (action.classEntry) {
        ctx.setState(patch<CustomizableClassTreeStateModel>({
          arbitraryNodes: patch<CustomizableClassTreeStateModel['arbitraryNodes']>({
            [action.classId]: action.classEntry
          })
        }));
      } else {
        throw new Error('Class entry cannot be found!');
      }
    }

    const draggedClassIds: string[] = ctx.getState().draggedClassIds ?? [];
    if (!draggedClassIds.includes(action.classId)) {
      this.addClassIdToDrag(ctx, action.classId);
    } else {
      ctx.patchState({
        draggedClassIds: draggedClassIds.filter(id => id !== action.classId)
      });
    }
  }

  @Action(CustomizableClassTreeAction.StartDrag)
  public startDrag(
    ctx: StateContext<CustomizableClassTreeStateModel>,
    action: CustomizableClassTreeAction.StartDrag
  ): void {
    if (ctx.getState().saveInProgress) {
      throw new Error('Save in progress!');
    }

    let classId: string = action.classId;
    const classEntry: ClassTreeNodeEntry = {...action.classEntry};

    if (!classId) {
      classId = CustomizableClassTreeXsState.NEW_CLASS_ID_PREFIX
        + (Array
          .from(crypto.getRandomValues(new Uint32Array(4)))
          .map(n => n.toString(36))
          .join(''));
      if (classEntry) {
        classEntry.id = classId;
      }
    }

    if (!ctx.getState().arbitraryParentChildrenMap[classId]) {
      ctx.setState(patch<CustomizableClassTreeStateModel>({
        arbitraryParentChildrenMap: patch<CustomizableClassTreeStateModel['arbitraryParentChildrenMap']>({
          [classId]: []
        })
      }));
    }

    if (!ctx.getState().arbitraryNodes.hasOwnProperty(classId)) {
      if (classEntry) {
        ctx.setState(patch<CustomizableClassTreeStateModel>({
          arbitraryNodes: patch<CustomizableClassTreeStateModel['arbitraryNodes']>({
            [classId]: classEntry
          })
        }));
      } else {
        throw new Error('Class entry cannot be found!');
      }
    }

    const draggedClassIds: string[] = ctx.getState().draggedClassIds ?? [];
    if (!draggedClassIds.includes(classId)) {
      this.addClassIdToDrag(ctx, classId);
    }

    ctx.patchState({dragInProgress: true});
  }

  @Action(CustomizableClassTreeAction.EndDrag)
  public endDrag(ctx: StateContext<CustomizableClassTreeStateModel>): void {
    this.closeEmptyColumns(
      ctx,
      CustomizableClassTreeXsState.getParentChildrenMapWithCommands(
        ctx.getState().arbitraryParentChildrenMap,
        ctx.getState().commands,
        ctx.getState().undo
      )
    );

    ctx.patchState({
      dragInProgress: false,
      draggedClassIds: []
    });
  }

  @Action(CustomizableClassTreeAction.Drop)
  public drop(
    ctx: StateContext<CustomizableClassTreeStateModel>,
    action: CustomizableClassTreeAction.Drop
  ): void {
    if (ctx.getState().saveInProgress) {
      throw new Error('Save in progress!');
    }

    const draggedClassIds: string[] = ctx.getState().draggedClassIds ?? [];

    if (draggedClassIds.length === 0) {
      return;
    }

    this.addCommand(
      ctx,
      new ClassTreeMoveCommand(
        draggedClassIds,
        action.parentId ?? CustomizableClassTreeXsState.ROOT_KEY,
        action.placeBefore
      )
    );

    this.store.dispatch(new CustomizableClassTreeAction.EndDrag());
  }

  @Action(CustomizableClassTreeAction.Delete)
  public delete(
    ctx: StateContext<CustomizableClassTreeStateModel>,
    action: CustomizableClassTreeAction.Delete
  ): void {
    if (!action.classId) {
      throw new Error('Class id cannot be null!');
    }

    if (ctx.getState().saveInProgress) {
      throw new Error('Save in progress!');
    }

    this.addCommand(
      ctx,
      new ClassTreeDeleteCommand(
        action.classId,
        action.parentId ?? CustomizableClassTreeXsState.ROOT_KEY
      )
    );

    this.closeEmptyColumns(
      ctx,
      CustomizableClassTreeXsState.getParentChildrenMapWithCommands(
        ctx.getState().arbitraryParentChildrenMap,
        ctx.getState().commands,
        ctx.getState().undo
      )
    );

    ctx.patchState({
      dragInProgress: false,
      draggedClassIds: []
    });
  }

  @Action(CustomizableClassTreeAction.Rename)
  public rename(
    ctx: StateContext<CustomizableClassTreeStateModel>,
    action: CustomizableClassTreeAction.Rename
  ): void {
    if (!action.classId) {
      throw new Error('Class Id cannot be null!');
    }

    if (!action.nameTranslation) {
      throw new Error('Name translation cannot be null!');
    }

    if (ctx.getState().saveInProgress) {
      throw new Error('Save in progress!');
    }

    this.addCommand(
      ctx,
      new ClassTreeRenameCommand(
        action.classId,
        action.nameTranslation
      )
    );
  }

  @Action(CustomizableClassTreeAction.Undo)
  public undo(ctx: StateContext<CustomizableClassTreeStateModel>): void {
    if (ctx.getState().saveInProgress) {
      throw new Error('Save in progress!');
    }

    if (ctx.getState().commands?.length > ctx.getState().undo) {
      ctx.patchState({
        undo: ctx.getState().undo + 1,
      });
    }
  }

  @Action(CustomizableClassTreeAction.Redo)
  public redo(ctx: StateContext<CustomizableClassTreeStateModel>): void {
    if (ctx.getState().saveInProgress) {
      throw new Error('Save in progress!');
    }

    if (ctx.getState().undo > 0) {
      ctx.patchState({
        undo: ctx.getState().undo - 1,
      });
    }
  }

  @Action(CustomizableClassTreeAction.DeleteTree)
  public async deleteTree(ctx: StateContext<CustomizableClassTreeStateModel>) {
    this.requestService.deleteClassTree(ctx.getState().treeId).subscribe();
  }

  @Action(CustomizableClassTreeAction.Save)
  public save(
    ctx: StateContext<CustomizableClassTreeStateModel>
  ): Observable<void> {
    if (ctx.getState().saveInProgress) {
      return throwError(() => new Error('Save in progress!'));
    }

    const commands: ClassTreeCommand[] = ctx.getState().commands;
    const undo: number = ctx.getState().undo;

    const parentChildrenMap: Map<string, (string | null)[]> = CustomizableClassTreeXsState
      .getParentChildrenMapWithCommands(
        ctx.getState().arbitraryParentChildrenMap,
        commands,
        undo
      );

    const nodes: Map<string, ClassTreeNodeEntry> = CustomizableClassTreeXsState
      .getNodesWithCommands(
        ctx.getState().arbitraryNodes,
        commands,
        undo
      );

    const renameCommands: ClassTreeRenameCommand[] =
      (undo > 0 ? commands.slice(0, commands.length - undo) : commands)
        .filter(command =>
          command instanceof ClassTreeRenameCommand && command.type == ClassTreeCommandType.RENAME
        ) as ClassTreeRenameCommand[];

    const translationMap: Map<string, TranslationCodeView> = new Map();

    for (const renameCommand of renameCommands) {
      translationMap.set(renameCommand.classId, renameCommand.nameTranslation);
    }

    const treeNewEntryMap: Map<string, ClassTreeNodeEntry> = new Map();

    for (const createdNode of nodes.keys()) {
      const isNewClass: boolean = createdNode.includes(CustomizableClassTreeXsState.NEW_CLASS_ID_PREFIX);
      const renamed: boolean = translationMap.has(createdNode);
      if (isNewClass && !renamed) {
        treeNewEntryMap.set(createdNode, nodes.get(createdNode));
        translationMap.set(createdNode, nodes.get(createdNode).nameTranslation);
      }
    }

    const startDate: Date = new Date();
    ctx.patchState({saveInProgress: true});

    return this.requestService.updateClassTree(
      ctx.getState().orgId,
      ctx.getState().treeId,
      parentChildrenMap,
      translationMap,
      treeNewEntryMap,
      this.app.data.getLang()
    ).pipe(
      delay(calcDefaultTimeoutOf(startDate)),
      finalize(() => ctx.patchState({saveInProgress: false})),
      finalize(() => this.store.dispatch(new CustomizableClassTreeAction.LoadClassTree(
        ctx.getState().orgId,
        ctx.getState().treeId,
        ctx.getState().treeInternalCode)
      )),
      map(() => {
        const arbitraryParentChildrenMap: ClassTreeParentChildrenMap = {};
        for (const [id, node] of parentChildrenMap.entries()) {
          arbitraryParentChildrenMap[id] = node;
        }

        const arbitraryNodes: ClassTreeNodeMap = {};
        for (const [id, node] of nodes.entries()) {
          arbitraryNodes[id] = node;
        }

        ctx.patchState({
          arbitraryParentChildrenMap,
          arbitraryNodes,
          commands: [],
          undo: 0
        });
      })
    );
  }

  @Action([
    CustomizableClassTreeAction.ResetState,
    ResetStateDefaults
  ])
  public resetState(ctx: StateContext<CustomizableClassTreeStateModel>): void {
    ctx.setState(getDefaults());
  }
  //endregion

  //region HELPERS
  private fetchTree(ctx: StateContext<CustomizableClassTreeStateModel>): Observable<void> {
    const requestStartDate: Date = new Date();
    return forkJoin({
      treeNodes: this.requestService.getTreeBranch(null, null,
        ctx.getState().treeId, ctx.getState().orgId),
      systemTreeNodes: this.requestService.getTreeBranch(null, null,
        null, ctx.getState().orgId)
    }).pipe(
      delay(calcDefaultTimeoutOf(requestStartDate)),
      map(({treeNodes, systemTreeNodes}) => {
        const allClassNodeInfos = Array.from(
          new Map(
            [...systemTreeNodes, ...treeNodes].map(node => [node.id, node])
          ).values()
        );
        ctx.setState(patch<CustomizableClassTreeStateModel>({
          arbitraryParentChildrenMap: patch<CustomizableClassTreeStateModel['arbitraryParentChildrenMap']>(
            treeNodes.reduce(
              (acc, node) => {
                // Map each node's own children
                acc[node.id] = node.childrenIds ?? [];
                return acc;
              },
              {
                // Root level: nodes that are not anyone's child
                [CustomizableClassTreeXsState.ROOT_KEY]: (() => {
                  const allChildIds = new Set(treeNodes.flatMap(n => n.childrenIds));
                  return treeNodes
                    .filter(n => !allChildIds.has(n.id))
                    .map(n => n.id);
                })()
              } as CustomizableClassTreeStateModel['arbitraryParentChildrenMap']
            )
          ),
          arbitraryNodes: patch<CustomizableClassTreeStateModel['arbitraryNodes']>(
            treeNodes.reduce(
              (acc, node) => {
                acc[node.id] = node;
                return acc;
              },
              {} as ClassTreeNodeMap
            )
          ),
          allClassNodeInfos,
          requiredClassIds: allClassNodeInfos
            .filter(node => node.required)
            .map(node => node.id),
          initialized: true
        }));
      })
    );
  }

  private addCommand(ctx: StateContext<CustomizableClassTreeStateModel>, command: ClassTreeCommand): void {
    const allCommands: ClassTreeCommand[] = ctx.getState().commands;
    const undo: number = ctx.getState().undo;

    ctx.patchState({
      commands: [
        ...(ctx.getState().undo > 0 ? allCommands.slice(0, allCommands.length - undo) : allCommands),
        command
      ],
      undo: 0
    });
  }

  private addClassIdToDrag(
    ctx: StateContext<CustomizableClassTreeStateModel>,
    classId: string
  ): void {
    const parentChildrenMap: Map<string, (string | null)[]>
      = CustomizableClassTreeXsState.getParentChildrenMapWithCommands(
        ctx.getState().arbitraryParentChildrenMap,
        ctx.getState().commands,
        ctx.getState().undo
      );

    let newDraggedClassIds: string[] =  [...(ctx.getState().draggedClassIds ?? [])];

    // Check if the parent is not already in the selection
    const columnParentIds: string[] = ctx.getState().columnParentIds ?? [];
    const parentIndex: number = columnParentIds.findIndex((id: string) =>
      parentChildrenMap?.get(id).includes(classId)
    );

    if (parentIndex != -1) {
      const parentIds: string[] = columnParentIds.slice(0, parentIndex + 1);
      if (newDraggedClassIds.some(draggedClassId => parentIds.includes(draggedClassId))) {
        return;
      }
    }

    // Remove all children
    const children: Set<string> = CustomizableClassTreeXsState.getAllChildren(parentChildrenMap, classId);
    newDraggedClassIds = newDraggedClassIds.filter(classId => !children.has(classId));

    newDraggedClassIds.push(classId);

    ctx.patchState({draggedClassIds: newDraggedClassIds});
  }

  private closeEmptyColumns(
    ctx: StateContext<CustomizableClassTreeStateModel>,
    parentChildrenMap: Map<string, (string | null)[]>
  ): void {
    const columnParentIds: string[] = ctx.getState().columnParentIds ?? [];
    if (columnParentIds.length > 0) {
      const parentIndexToHide: number = columnParentIds
        .findIndex(parentId => parentChildrenMap.get(parentId)?.length === 0);
      if (parentIndexToHide != -1) {
        ctx.patchState({
          columnParentIds: columnParentIds.slice(0, parentIndexToHide)
        });
      }
    }
  }
  //endregion

  //region STATIC HELPERS

  private static getNodesWithCommands(
    arbitraryNodes: ClassTreeNodeMap,
    allCommands: ClassTreeCommand[],
    undo: number
  ): Map<string, ClassTreeNodeEntry> {
    const nodes = new Map<string, ClassTreeNodeEntry>(Object.entries(arbitraryNodes ?? {}));

    const commands: ClassTreeCommand[] =
      (undo > 0 ? allCommands.slice(0, allCommands.length - undo) : allCommands)
        .filter(command => command.type == ClassTreeCommandType.RENAME);

    for (const command of commands) {
      if (command instanceof ClassTreeRenameCommand && command.type === ClassTreeCommandType.RENAME) {
        nodes.set(
          command.classId,
          {
            ...nodes.get(command.classId),
            nameTranslation: command.nameTranslation
          }
        );
      }
    }

    return nodes;
  }

  private static getParentChildrenMapWithCommands(
    arbitraryParentChildrenMap: { [id: string]: (string | null)[] },
    allCommands: ClassTreeCommand[],
    undo: number
  ): Map<string, (string | null)[]> {
    // 1. Determine which commands to apply
    const commands: ClassTreeCommand[] = undo > 0
      ? allCommands.slice(0, allCommands.length - undo)
      : allCommands;

    // 2. Initialize the Map from the initial object
    const parentChildrenMap = new Map<string, (string | null)[]>(Object.entries(arbitraryParentChildrenMap ?? {}));

    for (const command of commands) {

      // --- MOVE COMMAND ---
      if (command instanceof ClassTreeMoveCommand && command.type === ClassTreeCommandType.MOVE) {
        // Create a Set for O(1) lookups
        const idsToMove = new Set<string>(command.classIds);
        let remainingToRemove: number = idsToMove.size;

        // Remove classes from their current position
        for (const [parent, children] of parentChildrenMap.entries()) {
          // Use Set.has() instead of Array.includes() for instant lookups
          if (children.some(child => child !== null && idsToMove.has(child))) {

            const filteredChildren: string[] = children.filter(child => {
              if (child !== null && idsToMove.has(child)) {
                remainingToRemove--;
                return false; // Remove this child
              }
              return true; // Keep this child
            });

            parentChildrenMap.set(parent, filteredChildren);
          }

          // Early exit if we found all items
          if (remainingToRemove === 0) {
            break;
          }
        }

        // Add classes to the new position
        let newChildren: (string | null)[] = [];
        const currentParentChildren: string[] = parentChildrenMap.get(command.parentId) ?? [];

        if (!command.placeBefore) {
          newChildren = [...currentParentChildren, ...command.classIds];
        } else {
          for (const child of currentParentChildren) {
            if (child === command.placeBefore) {
              newChildren.push(...command.classIds);
            }
            newChildren.push(child);
          }
        }

        parentChildrenMap.set(command.parentId, newChildren);
      }

      // --- DELETE COMMAND ---
      if (command instanceof ClassTreeDeleteCommand && command.type === ClassTreeCommandType.DELETE) {
        const currentChildrenOfParent: string[] = parentChildrenMap.get(command.parentId);
        if (currentChildrenOfParent) {
          parentChildrenMap.set(
            command.parentId,
            currentChildrenOfParent.filter(child => child !== command.classId)
          );
        }

        const parentClassIdsToClear: string[] = [
          command.classId,
          ...CustomizableClassTreeXsState.getAllChildren(parentChildrenMap, command.classId)
        ];
        for (const classId of parentClassIdsToClear) {
          parentChildrenMap.set(classId, []);
        }
      }
    }

    return parentChildrenMap;
  }

  private static getChildParentMap(parentChildrenMap: Map<string, (string | null)[]>): Map<string, string> {
    const childParentMap = new Map<string, string>();

    for (const [parentId, children] of parentChildrenMap.entries()) {
      for (const childId of children) {
        if (childId !== null) {
          childParentMap.set(childId, parentId);
        }
      }
    }

    return childParentMap;
  }

  private static getAllChildren(
    parentChildrenMap: Map<string, (string | null)[]>,
    classId: string
  ): Set<string> {
    const resultSet: Set<string> = new Set<string>();
    const visited: Set<string> = new Set<string>();
    const stack: string[] = [classId];

    visited.add(classId);

    while (stack.length > 0) {
      const currentId: string = stack.pop();
      const children: (string | null)[] | undefined = parentChildrenMap.get(currentId);

      if (children) {
        for (const childId of children) {
          if (childId !== null && !visited.has(childId)) {
            visited.add(childId);
            resultSet.add(childId);
            stack.push(childId);
          }
        }
      }
    }

    return resultSet;
  }

  private static getAllClassIdsInTree(
    nodes: ClassTreeNodeMap,
    parentChildrenMap: Map<string, (string | null)[]>
  ): Set<string> {
    const allClassIds = new Set<string>();

    for (const classIds of parentChildrenMap.values()) {
      for (const classId of classIds) {
        let children: string[];
        const isClassLoaded = parentChildrenMap.has(classId);

        if (isClassLoaded) {
          children = parentChildrenMap.get(classId) ?? [];
        } else {
          children = [...(nodes[classId]?.childrenIds ?? [])];
        }
        allClassIds.add(classId);
        children.forEach(childId => allClassIds.add(childId));
      }
    }

    return allClassIds;
  }

  private static getRequiredParentIds(
    nodes: ClassTreeNodeMap,
    childParentMap: Map<string, string>,
    requiredClassIds: string[],
  ): Set<string> {
    const requiredParentIds: Set<string> = new Set<string>();
    const unmappedRequiredClassIds: Set<string> = new Set<string>();

    for (const requiredClassId of requiredClassIds ?? []) {
      let currentId: string = requiredClassId;

      if (childParentMap.has(currentId)) {
        while (childParentMap.has(currentId)) {
          const parentId: string = childParentMap.get(currentId);

          if (requiredParentIds.has(parentId)) {
            break;
          }

          requiredParentIds.add(parentId);
          currentId = parentId;
        }
      } else {
        unmappedRequiredClassIds.add(requiredClassId);
      }
    }

    if (unmappedRequiredClassIds.size > 0) {
      for (const node of Object.values(nodes ?? {})) {
        if (node.childrenIds?.some(childId => unmappedRequiredClassIds.has(childId))) {
          requiredParentIds.add(node.id);
        }
      }
    }

    return requiredParentIds;
  }
  //endregion
}
